import React from 'react';
import { User } from '@phosphor-icons/react';

/**
 * Read-only list of currently registered players + sub pool for a session.
 *
 *   • Shown on the player-facing session detail page under the Roster block.
 *   • Renders first + last name only — no email, no phone, no private data.
 *   • Sub Pool section is hidden entirely when there are no subs (typical
 *     for pre-start sessions).
 *
 * Props:
 *   registrations: Array of { id, role, status, full_name, first_name,
 *                             last_name, initial_rank, registered_at }
 *                  (sorted players-first by initial_rank, then subs by date —
 *                  this is the same shape the backend already returns).
 */
export default function RegisteredPlayersList({ registrations = [] }) {
  // Hide inactive (removed/withdrawn) registrations from the user-facing list.
  const active = registrations.filter(
    (r) => (r.status || 'active') === 'active',
  );
  const players = active.filter((r) => r.role === 'player');
  const subs = active.filter((r) => r.role === 'sub');

  return (
    <section
      className="bg-[#fbfaf8] border border-[#e3dcd1] rounded-2xl p-4 space-y-3"
      data-testid="registered-players-block"
    >
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-[#6a766e]">
          Registered Players
        </div>
        <span
          data-testid="registered-players-count"
          className="text-[11px] px-2 py-0.5 rounded-full bg-[#015e3f]/10 text-[#015e3f] font-medium"
        >
          {players.length} registered
        </span>
      </div>

      {players.length === 0 ? (
        <p
          className="text-xs text-[#6a766e]"
          data-testid="registered-players-empty"
        >
          No players registered yet.
        </p>
      ) : (
        <ul
          className="space-y-1.5"
          data-testid="registered-players-list"
        >
          {players.map((r) => (
            <PlayerRow key={r.id} reg={r} testIdPrefix="registered-player" />
          ))}
        </ul>
      )}

      {subs.length > 0 && (
        <div className="pt-2 border-t border-[#e3dcd1]/70 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-wider text-[#6a766e]">
              Sub Pool
            </div>
            <span
              data-testid="sub-pool-count"
              className="text-[11px] px-2 py-0.5 rounded-full bg-[#b8862a]/10 text-[#8a6418] font-medium"
            >
              {subs.length} {subs.length === 1 ? 'sub' : 'subs'}
            </span>
          </div>
          <ul
            className="space-y-1.5"
            data-testid="sub-pool-list"
          >
            {subs.map((r) => (
              <PlayerRow key={r.id} reg={r} testIdPrefix="sub-pool-player" />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function displayName(reg) {
  const full = (reg.full_name || '').trim();
  if (full) return full;
  const parts = [reg.first_name, reg.last_name].filter(Boolean).join(' ').trim();
  return parts || 'Club Member';
}

function PlayerRow({ reg, testIdPrefix }) {
  return (
    <li
      data-testid={`${testIdPrefix}-${reg.id}`}
      className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-white border border-[#e3dcd1]"
    >
      <div className="shrink-0 w-6 h-6 rounded-full bg-[#015e3f]/10 text-[#015e3f] flex items-center justify-center">
        <User size={12} weight="fill" />
      </div>
      <div className="text-sm text-[#013523] truncate">
        {displayName(reg)}
      </div>
    </li>
  );
}
